import { useState, useSyncExternalStore } from 'react';
import { AI_CHARACTERS, type AiCharacterComponent } from './AiCharacters';

const AI_CHARACTER_STORAGE_KEY = 'nodon:ai-character';
/** A new character for every card that shows one (Henrik 2026-10-16). */
export const RANDOM_AI_CHARACTER_ID = 'random';
export const DEFAULT_AI_CHARACTER_ID = RANDOM_AI_CHARACTER_ID;
const CHANGED = 'nodon:ai-character-changed';

const isCharacterId = (value: string | null): value is string =>
  value === RANDOM_AI_CHARACTER_ID ||
  AI_CHARACTERS.some((row) => row.id === value);

/** The character picked in the admin menu, or random when none or an unknown one is stored. */
export const readStoredAiCharacterId = (): string => {
  if (typeof window === 'undefined') return DEFAULT_AI_CHARACTER_ID;
  try {
    const raw = window.localStorage.getItem(AI_CHARACTER_STORAGE_KEY);
    return isCharacterId(raw) ? raw : DEFAULT_AI_CHARACTER_ID;
  } catch {
    return DEFAULT_AI_CHARACTER_ID;
  }
};

/** Store the pick for this browser and redraw the cards that show it. */
export const writeStoredAiCharacterId = (id: string): void => {
  if (!isCharacterId(id)) return;
  try {
    window.localStorage.setItem(AI_CHARACTER_STORAGE_KEY, id);
  } catch {
    // Storage blocked: the pick still applies until reload.
  }
  window.dispatchEvent(new Event(CHANGED));
};

const subscribe = (onChange: () => void) => {
  window.addEventListener(CHANGED, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(CHANGED, onChange);
    window.removeEventListener('storage', onChange);
  };
};

const randomCharacter = (): AiCharacterComponent =>
  AI_CHARACTERS[Math.floor(Math.random() * AI_CHARACTERS.length)]!.Component;

/**
 * The character picked in the admin menu, updated when the pick changes. On
 * random, each component that calls this draws its own character once and
 * keeps it, so an import keeps its character for its whole life.
 */
export const useStoredAiCharacter = (): AiCharacterComponent => {
  const [random] = useState(randomCharacter);
  const id = useSyncExternalStore(
    subscribe,
    readStoredAiCharacterId,
    () => DEFAULT_AI_CHARACTER_ID,
  );
  return AI_CHARACTERS.find((row) => row.id === id)?.Component ?? random;
};
